import { useState } from 'react';
import { Link } from 'react-router-dom';

const plans = [
  {
    name: 'Starter',
    badge: 'Free Forever',
    price: 0,
    highlight: 'Best for new and first-time event hosts',
    color: 'from-teal-500 to-cyan-500',
    badgeBg: 'bg-teal-50 text-teal-700 border-teal-200',
    iconBg: 'bg-teal-100 text-teal-600',
    popular: false,
    features: [
      { text: 'Up to 3 events per month', icon: 'ri-calendar-check-line' },
      { text: '10% platform commission', icon: 'ri-percent-line' },
      { text: 'Social follow gate (up to 2 accounts)', icon: 'ri-user-add-line' },
      { text: 'Online store (up to 10 products)', icon: 'ri-store-2-line' },
      { text: 'Basic analytics', icon: 'ri-bar-chart-line' },
      { text: 'Email support', icon: 'ri-mail-line' },
    ],
  },
  {
    name: 'Growth',
    badge: 'Most Popular',
    price: 19,
    highlight: 'Perfect for growing event organizers',
    color: 'from-indigo-500 to-violet-500',
    badgeBg: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    iconBg: 'bg-indigo-100 text-indigo-600',
    popular: true,
    features: [
      { text: 'Unlimited events', icon: 'ri-infinity-line' },
      { text: '5% platform commission', icon: 'ri-percent-line' },
      { text: 'Social follow gate (up to 5 accounts)', icon: 'ri-user-add-line' },
      { text: 'Online store (unlimited products)', icon: 'ri-store-2-line' },
      { text: 'Advanced analytics + email campaigns', icon: 'ri-line-chart-line' },
      { text: 'Scheduled email reminders', icon: 'ri-mail-send-line' },
      { text: 'Priority support', icon: 'ri-mail-star-line' },
    ],
  },
  {
    name: 'Pro Partner',
    badge: 'For Power Users',
    price: 39,
    highlight: 'Everything you need to scale',
    color: 'from-rose-500 to-pink-500',
    badgeBg: 'bg-rose-50 text-rose-700 border-rose-200',
    iconBg: 'bg-rose-100 text-rose-600',
    popular: false,
    features: [
      { text: 'Everything in Growth', icon: 'ri-check-double-line' },
      { text: '2% platform commission', icon: 'ri-percent-line' },
      { text: 'Homepage spotlight & featured placement', icon: 'ri-flashlight-line' },
      { text: 'Unlimited social follow accounts', icon: 'ri-user-add-line' },
      { text: 'Custom event branding', icon: 'ri-palette-line' },
      { text: 'Team access (multiple organizers)', icon: 'ri-team-line' },
      { text: 'Dedicated support', icon: 'ri-customer-service-2-line' },
    ],
  },
];

const comparisonRows = [
  {
    category: 'Events',
    feature: 'Events per month',
    starter: '3 events',
    growth: 'Unlimited',
    pro: 'Unlimited',
  },
  {
    category: 'Fees',
    feature: 'Platform commission',
    starter: '10%',
    growth: '5%',
    pro: '2%',
  },
  {
    category: 'Social',
    feature: 'Social follow gate accounts',
    starter: 'Up to 2',
    growth: 'Up to 5',
    pro: 'Unlimited',
  },
  {
    category: 'Store',
    feature: 'Online store products',
    starter: 'Up to 10',
    growth: 'Unlimited',
    pro: 'Unlimited',
  },
  {
    category: 'Analytics',
    feature: 'Analytics',
    starter: 'Basic',
    growth: 'Advanced',
    pro: 'Advanced',
  },
  {
    category: 'Marketing',
    feature: 'Email campaigns',
    starter: false,
    growth: true,
    pro: true,
  },
  {
    category: 'Marketing',
    feature: 'Scheduled email reminders',
    starter: false,
    growth: true,
    pro: true,
  },
  {
    category: 'Visibility',
    feature: 'Homepage spotlight',
    starter: false,
    growth: false,
    pro: true,
  },
  {
    category: 'Branding',
    feature: 'Custom event branding',
    starter: false,
    growth: false,
    pro: true,
  },
  {
    category: 'Team',
    feature: 'Team / multi-organizer access',
    starter: false,
    growth: false,
    pro: true,
  },
  {
    category: 'Support',
    feature: 'Support level',
    starter: 'Email',
    growth: 'Priority',
    pro: 'Dedicated',
  },
];

const faqs = [
  {
    q: 'Can I cancel anytime?',
    a: 'Yes, absolutely. You can cancel your subscription at any time from your dashboard. There are no cancellation fees or lock-in periods. Your plan stays active until the end of the billing period you already paid for.',
  },
  {
    q: 'What exactly counts as a "platform commission"?',
    a: 'The platform commission is a percentage taken from each paid ticket sale or store product purchase processed through our platform. For example, on the Growth plan (5%), if you sell a $100 ticket, you receive $95. Free events and free products are never charged a commission.',
  },
  {
    q: 'Can I upgrade or downgrade my plan later?',
    a: 'Yes. You can upgrade to a higher plan at any time and the new rate takes effect immediately. Downgrading is also possible — it takes effect at the start of your next billing cycle.',
  },
  {
    q: 'Is there a free trial for paid plans?',
    a: 'The Starter plan is free forever with no credit card required, so you can explore the platform risk-free. Paid plans don\'t currently have a trial period, but you can start on Starter and upgrade once you\'re ready.',
  },
  {
    q: 'What payment methods do you accept for subscriptions?',
    a: 'We accept card payments, bank transfers, and mobile money through our secure payment partners. All transactions are encrypted and processed securely.',
  },
  {
    q: 'What happens to my events if I downgrade to Starter?',
    a: 'Your existing events remain live and accessible. However, going forward you\'ll be limited to 3 events per month and the 10% commission rate will apply to new ticket sales. No data is ever deleted when you change plans.',
  },
  {
    q: 'Does the yearly plan auto-renew?',
    a: 'Yes, yearly plans auto-renew at the end of the 12-month period. You\'ll receive an email reminder 7 days before renewal so you can make any changes if needed.',
  },
];

type CellValue = string | boolean;

function ComparisonCell({ value, planColor }: { value: CellValue; planColor: string }) {
  if (typeof value === 'boolean') {
    return (
      <div className="flex justify-center">
        {value ? (
          <div className={`w-6 h-6 flex items-center justify-center rounded-full ${planColor}`}>
            <i className="ri-check-line text-white text-xs"></i>
          </div>
        ) : (
          <div className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100">
            <i className="ri-close-line text-gray-400 text-xs"></i>
          </div>
        )}
      </div>
    );
  }
  return <span className="text-sm text-gray-700 font-medium">{value}</span>;
}

export default function PricingSection() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const getPrice = (basePrice: number) => {
    if (basePrice === 0) return 0;
    return billingCycle === 'yearly' ? Math.round((basePrice * 10) / 12) : basePrice;
  };

  const formatPrice = (price: number) => {
    if (price === 0) return '0';
    return price % 1 === 0 ? price.toString() : price.toFixed(2);
  };

  return (
    <section id="pricing" className="py-16 sm:py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-4 sm:mb-6">
            <i className="ri-price-tag-3-line text-teal-600"></i>
            <span className="text-xs sm:text-sm font-semibold text-teal-700">Simple Pricing</span>
          </div>
          <h4>
            <a href="#pricing" className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 block">
              Plans That{' '}
              <span className="bg-gradient-to-r from-teal-500 to-cyan-500 bg-clip-text text-transparent">
                Grow With You
              </span>
            </a>
          </h4>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
            Start free, upgrade when you&apos;re ready. No hidden fees, cancel anytime.
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-3 sm:gap-4 mt-8 sm:mt-10">
            <span className={`text-xs sm:text-sm font-semibold transition-colors ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-400'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              className={`relative w-12 h-6 sm:w-14 sm:h-7 rounded-full transition-colors cursor-pointer ${billingCycle === 'yearly' ? 'bg-teal-500' : 'bg-gray-300'}`}
              aria-label="Toggle billing cycle"
            >
              <div className={`absolute top-0.5 w-5 h-5 sm:w-6 sm:h-6 bg-white rounded-full shadow-md transition-transform ${billingCycle === 'yearly' ? 'translate-x-6 sm:translate-x-7' : 'translate-x-0.5'}`} />
            </button>
            <span className={`text-xs sm:text-sm font-semibold transition-colors ${billingCycle === 'yearly' ? 'text-gray-900' : 'text-gray-400'}`}>
              Yearly
            </span>
            {billingCycle === 'yearly' && (
              <span className="px-2.5 sm:px-3 py-1 bg-teal-100 text-teal-700 text-[10px] sm:text-xs font-bold rounded-full animate-pulse whitespace-nowrap">
                Save ~17%
              </span>
            )}
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 items-start mb-16 sm:mb-20">
          {plans.map((plan) => {
            const price = getPrice(plan.price);
            return (
              <div
                key={plan.name}
                className={`relative rounded-2xl bg-white transition-all duration-300 hover:-translate-y-2 ${
                  plan.popular
                    ? 'ring-2 ring-indigo-500 md:scale-[1.03]'
                    : 'border border-gray-100'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2">
                    <div className="px-4 sm:px-5 py-1 sm:py-1.5 bg-gradient-to-r from-indigo-500 to-violet-500 text-white text-[10px] sm:text-xs font-bold rounded-full whitespace-nowrap">
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="p-6 sm:p-8">
                  <div className="mb-5 sm:mb-6">
                    <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full border ${plan.badgeBg}`}>
                      {plan.badge}
                    </span>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mt-3">{plan.name}</h3>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">{plan.highlight}</p>
                  </div>

                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-base sm:text-lg font-semibold text-gray-500">$</span>
                    <span className={`text-4xl sm:text-5xl font-extrabold bg-gradient-to-r ${plan.color} bg-clip-text text-transparent`}>
                      {formatPrice(price)}
                    </span>
                    <span className="text-gray-500 text-xs sm:text-sm font-medium">/month</span>
                  </div>

                  {/* Yearly savings badge */}
                  {plan.price > 0 && (
                    <div className="mb-6 sm:mb-8 h-6">
                      {billingCycle === 'yearly' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 text-[10px] sm:text-xs font-bold rounded-full">
                          <i className="ri-check-line"></i>
                          Saving ${plan.price * 2}/yr vs monthly
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-600 text-[10px] sm:text-xs font-semibold rounded-full border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors" onClick={() => setBillingCycle('yearly')}>
                          <i className="ri-flashlight-line"></i>
                          Save ${plan.price * 2}/yr — switch to yearly
                        </span>
                      )}
                    </div>
                  )}
                  {plan.price === 0 && <div className="mb-6 sm:mb-8 h-6"></div>}

                  <Link
                    to="/subscribe"
                    className={`block w-full text-center py-3 sm:py-3.5 rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      plan.popular
                        ? 'bg-gradient-to-r from-indigo-500 to-violet-500 text-white hover:opacity-90'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                  >
                    {plan.price === 0 ? 'Get Started Free' : 'Start Free Trial'}
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>

                  <div className="border-t border-gray-100 my-6 sm:my-8"></div>

                  <p className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 sm:mb-4">
                    What&apos;s included
                  </p>
                  <ul className="space-y-3 sm:space-y-3.5">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2.5 sm:gap-3">
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-md flex-shrink-0 ${plan.iconBg}`}>
                          <i className={`${feature.icon} text-[10px] sm:text-xs`}></i>
                        </div>
                        <span className="text-xs sm:text-sm text-gray-700 leading-snug">{feature.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Comparison Table ── */}
        <div id="pricing-compare" className="mb-16 sm:mb-20 scroll-mt-24">
          <div className="text-center mb-8 sm:mb-10">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Full Feature Comparison
            </h3>
            <p className="text-sm sm:text-base text-gray-500">See exactly what you get on each plan</p>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-4 px-5 sm:px-6 text-sm font-semibold text-gray-500 w-[40%]">Feature</th>
                  <th className="text-center py-4 px-4 sm:px-6 w-[20%]">
                    <div className="text-sm font-bold text-gray-900">Starter</div>
                    <div className="text-xs text-gray-400 font-normal mt-0.5">Free</div>
                  </th>
                  <th className="text-center py-4 px-4 sm:px-6 w-[20%] bg-indigo-50/60">
                    <div className="text-sm font-bold text-indigo-700">Growth</div>
                    <div className="text-xs text-indigo-400 font-normal mt-0.5">$19/mo</div>
                  </th>
                  <th className="text-center py-4 px-4 sm:px-6 w-[20%]">
                    <div className="text-sm font-bold text-gray-900">Pro Partner</div>
                    <div className="text-xs text-gray-400 font-normal mt-0.5">$39/mo</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`border-b border-gray-50 last:border-0 transition-colors hover:bg-gray-50/50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
                  >
                    <td className="py-3.5 px-5 sm:px-6">
                      <span className="text-sm text-gray-700">{row.feature}</span>
                    </td>
                    <td className="py-3.5 px-4 sm:px-6 text-center">
                      <ComparisonCell value={row.starter} planColor="bg-teal-500" />
                    </td>
                    <td className="py-3.5 px-4 sm:px-6 text-center bg-indigo-50/40">
                      <ComparisonCell value={row.growth} planColor="bg-indigo-500" />
                    </td>
                    <td className="py-3.5 px-4 sm:px-6 text-center">
                      <ComparisonCell value={row.pro} planColor="bg-rose-500" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── FAQ Section ── */}
        <div className="mb-16 sm:mb-20">
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full mb-4">
              <i className="ri-question-answer-line text-amber-600"></i>
              <span className="text-xs sm:text-sm font-semibold text-amber-700">FAQ</span>
            </div>
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Most Asked Questions
            </h3>
            <p className="text-sm sm:text-base text-gray-500 max-w-xl mx-auto">
              Everything you need to know before picking a plan
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="bg-white border border-gray-100 rounded-xl overflow-hidden transition-all duration-200"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-4 sm:py-5 text-left cursor-pointer hover:bg-gray-50/50 transition-colors"
                >
                  <span className="text-sm sm:text-base font-semibold text-gray-900">{faq.q}</span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 ${openFaq === idx ? 'bg-teal-100 text-teal-600 rotate-180' : 'bg-gray-100 text-gray-500'}`}>
                    <i className="ri-arrow-down-s-line text-base"></i>
                  </div>
                </button>
                {openFaq === idx && (
                  <div className="px-5 sm:px-6 pb-5 sm:pb-6">
                    <div className="border-t border-gray-100 pt-4">
                      <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Note */}
        <div className="text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-3 sm:gap-6 px-6 sm:px-8 py-4 bg-white rounded-2xl border border-gray-100">
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
              <i className="ri-shield-check-line text-teal-500 text-base sm:text-lg"></i>
              <span>No credit card required</span>
            </div>
            <div className="hidden sm:block w-px h-5 bg-gray-200"></div>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
              <i className="ri-refresh-line text-teal-500 text-base sm:text-lg"></i>
              <span>Cancel anytime</span>
            </div>
            <div className="hidden sm:block w-px h-5 bg-gray-200"></div>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
              <i className="ri-lock-line text-teal-500 text-base sm:text-lg"></i>
              <span>Secure payments</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
